import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { notifyTokenExpired, notifyTokenRefreshed } from '@/utils/rn-bridge'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const buildLoginRedirectUrl = () => {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const encodedRedirect = encodeURIComponent(currentPath)

  if (window.location.pathname.startsWith('/pc')) {
    return `/pc/login?redirect=${encodedRedirect}`
  }

  // F17(2026-10-13): 모바일 로그인·라우터 가드는 returnUrl 을 쓴다(PC 는 redirect)
  return `/mobile/login?returnUrl=${encodedRedirect}`
}

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 요청 인터셉터 - JWT 토큰 자동 포함
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// 응답 인터셉터 - 토큰 만료 시 자동 갱신
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const responseBody = error.response?.data as { code?: number; message?: string } | undefined

    // 서버가 실제 4xx/5xx를 반환해도 기존 화면의 err.message 처리가 동일 메시지를 사용하도록 정규화한다.
    if (responseBody?.message) {
      error.message = responseBody.message
    }

    // 401 에러이고, 재시도하지 않은 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshTokenValue = localStorage.getItem('refreshToken')
      const accessTokenValue = localStorage.getItem('accessToken')

      // 토큰 자체가 없는 경우 (비로그인 상태) → 로그인 페이지로 이동
      if (!refreshTokenValue && !accessTokenValue) {
        window.location.href = buildLoginRedirectUrl()
        return Promise.reject(error)
      }

      try {
        if (!refreshTokenValue) {
          throw new Error('No refresh token')
        }

        // 토큰 갱신 요청
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
          refreshToken: refreshTokenValue,
        })

        const { accessToken, refreshToken: newRefreshToken } = response.data.data

        // 새 토큰 저장
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        // 앱(RN) 저장소도 새 토큰으로 — 안 하면 포그라운드 복귀 때 옛 토큰으로 덮여 강제 로그아웃된다
        notifyTokenRefreshed(accessToken, newRefreshToken)

        // 원래 요청 재시도
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }
        return apiClient(originalRequest)
      } catch (refreshError) {
        // 토큰 갱신 실패 - 로그아웃 처리
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('userId')
        localStorage.removeItem('userName')
        localStorage.removeItem('userType')
        localStorage.removeItem('clubMemberships')
        // RN AsyncStorage 토큰 클리어 요청 (만료 토큰 재주입 루프 방지)
        notifyTokenExpired()
        window.location.href = buildLoginRedirectUrl()
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
